import { GlobalExclusionsAppView } from '../view/GlobalExclusionsAppView';
import { templateGlobalExclusionsApp } from '../view/template';
import { GlobalExclusionsAppRepository } from '../repository/GlobalExclusionsAppRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { GlobalExclusionsAppWebComponent } from '../index';
import { GlobalExclusionsAppWebComponentSchema } from '../types';
import { GlobalExclusionsEvents } from '../../exclusionsPanel/types';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { ExclusionListDetailsPanelWebComponent } from '../../exclusionListDetailsPanel';
import { GridEvents } from '../../../grid/types/common';
import { formatDate } from '@nirvana/shared/time';
import { translateFn } from '../../../utils/translator.helper';

export class GlobalExclusionsAppController extends BaseWebComponentController {
  public component: GlobalExclusionsAppWebComponent;
  public dataRepository: GlobalExclusionsAppRepository;
  public view: GlobalExclusionsAppView;
  public viewConstructor: typeof GlobalExclusionsAppView;
  public dataRepositoryConstructor: typeof GlobalExclusionsAppRepository;

  constructor(component: GlobalExclusionsAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: GlobalExclusionsAppView,
      viewTemplate: templateGlobalExclusionsApp,
      dataRepositoryConstructor: GlobalExclusionsAppRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$exclusionsLists,
        event: GlobalExclusionsEvents.CREATE_NEW_EXCLUSION_LIST,
        callback: this.onCreateNewExclusionList,
      },
      {
        element: this.view.dom.$newExclusionList,
        event: GlobalExclusionsEvents.CREATE_NEW_EXCLUSION_LIST_SUCCESS,
        callback: this.onCreateNewExclusionListSuccess,
      },
      {
        element: this.view.dom.$exclusionsLists,
        event: GlobalExclusionsEvents.SHOW_ALL_EXCLUSION_LIST,
        callback: this.onShowAllExclusions,
      },
      {
        element: this.view.dom.$exclusionsLists,
        event: GlobalExclusionsEvents.SHOW_EXCLUSION_LIST,
        callback: this.onShowExclusionList,
      },
      {
        element: this.view.dom.$globalExclusions,
        event: GlobalExclusionsEvents.LINK_TITLE_CLICK,
        callback: this.onShowExclusionListDetails,
      },
      {
        element: this.view.dom.$globalExclusions,
        event: GlobalExclusionsEvents.ADD_EXCLUSIONS,
        callback: this.addExclusions,
      },
      {
        element: this.view.dom.$globalExclusions,
        event: GlobalExclusionsEvents.CONFIRM_DELETE_EXCLUSION_ITEM,
        callback: this.onConfirmDeleteExclusionItemDetails,
      },
      {
        element: this.view.dom.$globalExclusions,
        event: GridEvents.SET_PAGE_SIZE,
        callback: this.updateGridSettings,
      },
      {
        element: this.view.dom.$globalExclusions,
        event: GlobalExclusionsEvents.ASSIGN_ITEMS_TO_LISTS,
        callback: this.onAssignItemsToLists,
      },
      {
        element: this.component,
        event: GlobalExclusionsEvents.ASSIGN_LIST_TO_POLICIES,
        callback: this.onAssignListToPolicies,
      },
    ];
  }

  public updateGridSettings() {
    const globalExclusions = this.view.dom.$globalExclusions;
    const gridView = globalExclusions.controller?.view.dom.$gridView;
    gridView.controller.handleGridItemsLoad();
  }

  public addExclusions() {
    this.view.dom.$newExclusionList.isVisible = true;
    this.view.dom.$newExclusionList.listId = !this.component.activeExclusionList.value ? undefined : this.component.activeExclusionList.value;
  }

  private onCreateNewExclusionList(ev: WebComponentEvent) {
    this.view.showNewExclusionListForm();
  }

  private async onCreateNewExclusionListSuccess(ev: WebComponentEvent) {
    const { list } = ev.detail;
    list.refresh = true;
    if (list.value) {
      await this.onShowExclusionList(ev);
    } else {
      await this.onShowAllExclusions();
    }
  }

  public async onShowAllExclusions() {
    this.component.activeExclusionList = {label: '', value: ''};
  }

  private async onShowExclusionList(ev: WebComponentEvent) {
    const {list} = ev.detail;
    this.component.activeExclusionList = list;
  }

  public async showExclusionsGrid() {
    if (this.component.activeExclusionList.value && !this.component.activeExclusionList?.refresh) {
      this.view.dom.$exclusionsLists.setActiveListItem(!this.component.activeExclusionList.value ? null : this.component.activeExclusionList);
      this.view.dom.$globalExclusions.activeExclusionList = this.component.activeExclusionList;
    } else {
      await this.render();
    }
  }

  private async onShowExclusionListDetails(ev: WebComponentEvent) {
    const listId = this.component.activeExclusionList.value;

    // in case of action triggered on 'All Exclusions' grid, skip
    if (!listId) {
      return;
    }

    const { details, policies } = await this.getExclusionListDetails(listId);

    if (!details) {
      return;
    }

    const panel = this.buildDetailsPanel(details, policies);
    this.showDetailsPanel(panel);
  }

  private showDetailsPanel(panel) {
    const configurationProfiles = document.body.querySelector('gz-configuration-profiles');
    let configurationProfilesShadow;
    let sidemenu;

    if (configurationProfiles) {
      configurationProfilesShadow = configurationProfiles.shadowRoot;
      sidemenu = configurationProfilesShadow.querySelector('gz-side-menu');
    } else {
      sidemenu = new GzSideMenuWebComponent();
      configurationProfilesShadow = this.view.dom.$wrapper;
      configurationProfilesShadow.appendChild(sidemenu);
    }

    sidemenu.setMenuContent(panel);
    sidemenu.openSideMenu();
  }

  private buildDetailsPanel(details, policies) {
    const panel = new ExclusionListDetailsPanelWebComponent();
    panel.createdByTitle = translateFn('Created by');
    panel.createdOnTitle = translateFn('Created on');
    panel.editedByTitle = translateFn('Edited by');
    panel.editedOnTitle = translateFn('Edited on');
    panel.detailsTitle = translateFn('Details');
    panel.generalTitle = translateFn('General');
    panel.deleteButtonLabel = translateFn('Delete');
    panel.assignToPolicyButtonLabel = translateFn('Edit assignment');
    panel.shouldDisplayEditedDate = this.shouldDisplayEdited(details.created.date, details.updated.date);
    panel.listId = details._id;
    panel.name = details.name;
    panel.createdBy = details.created.userName;
    panel.createdOn = formatDate(details.created.date);
    panel.editedBy = details.updated.userName;
    panel.editedOn = formatDate(details.updated.date);
    panel.description = details.description;
    panel.policies = policies;

    return panel;
  }

  private shouldDisplayEdited(createdDate, updatedDate) {
    const date1 = new Date(createdDate),
      date2 = new Date(updatedDate);

    return date1.getTime() !== date2.getTime();
  }

  private async getExclusionListDetails(listId) {
    const companyId = Application.Main.getActiveCompany().id;
    let details;
    let policies;
    const response = await this.dataRepository.getListInfo(companyId, listId);

    if (response && response.success && response.data) {
      details = response.data?.details;
      policies = response.data?.policies;
    }

    return { details, policies };
  }

  private onConfirmDeleteExclusionItemDetails(ev: WebComponentEvent) {
    this.view.refreshGrid(this.component.activeExclusionList.value, false);
  }

  async getDataForView(): Promise<GlobalExclusionsAppWebComponentSchema> {
    const companyId = Application.Main.getActiveCompany().id;
    const response = await this.dataRepository.getGlobalExclusionsData(companyId);

    return {
      userLists: response?.userLists ?? [],
      companyLists: response?.companyLists ?? [],
      activeExclusionList: this.component.activeExclusionList,
    };
  }

  private onAssignItemsToLists() {
    this.view.refreshGrid(this.component.activeExclusionList.value, false);
  }

  private onAssignListToPolicies() {
    this.view.refreshGrid(this.component.activeExclusionList.value, false);
  }
}
